import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// LayoutDesigner - Visual editor for asymmetric grid layouts
// Micro-animations via CSS keyframes (BL-19: framer-motion removed).
import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useWidgetI18n } from "../context/WidgetI18nContext";
import styles from "./LayoutDesigner.module.css";
export const LayoutDesigner = ({ layout, onLayoutChange, onSaveAsPreset, availablePresets = [], onApplyPreset, onDeletePreset, isOpen, onClose, }) => {
    const { t } = useWidgetI18n();
    const [selectedSlotId, setSelectedSlotId] = useState(null);
    const [dragState, setDragState] = useState(null);
    const [showPresetModal, setShowPresetModal] = useState(false);
    const [presetName, setPresetName] = useState("");
    const containerRef = useRef(null);
    // Generate grid template areas from layout
    const templateAreas = useMemo(() => {
        if (!layout.templateAreas || layout.templateAreas.length === 0) {
            // Generate default symmetric grid
            return Array(layout.rows).fill(null).map((_, r) => Array(layout.cols).fill(null).map((_, c) => `chart-${r * layout.cols + c + 1}`).join(" "));
        }
        return layout.templateAreas;
    }, [layout.templateAreas, layout.rows, layout.cols]);
    // Get all slots from layout
    const slots = useMemo(() => {
        if (layout.areas) {
            return Object.entries(layout.areas).map(([areaName, slot]) => ({
                ...slot,
                areaName,
            }));
        }
        return layout.slots.map((slot, index) => ({
            ...slot,
            areaName: `chart-${index + 1}`,
        }));
    }, [layout.areas, layout.slots]);
    // Handle mouse down on slot edge for resizing
    const handleSlotMouseDown = useCallback((e, slot, edge) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setDragState({
            type: 'resize',
            slotId: slot.id,
            startX: e.clientX,
            startY: e.clientY,
            startRect: rect,
        });
    }, []);
    // Handle mouse down on slot for moving
    const handleSlotMoveMouseDown = useCallback((e, slot) => {
        if (e.target !== e.currentTarget)
            return;
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        setDragState({
            type: 'move',
            slotId: slot.id,
            startX: e.clientX,
            startY: e.clientY,
            startRect: rect,
        });
    }, []);
    // Handle global mouse move
    useEffect(() => {
        if (!dragState)
            return;
        const handleMouseMove = (e) => {
            const deltaX = e.clientX - dragState.startX;
            const deltaY = e.clientY - dragState.startY;
            // Update layout based on drag
            // This is a simplified version - in practice, you'd calculate new grid positions
        };
        const handleMouseUp = () => {
            setDragState(null);
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragState]);
    // Split slot horizontally
    const splitSlotHorizontal = useCallback((slotId) => {
        const slot = slots.find(s => s.id === slotId);
        if (!slot)
            return;
        // Create new layout with split slot
        const newLayout = {
            ...layout,
            rows: layout.rows + 1,
            rowSizes: [...layout.rowSizes, '1fr'],
            slots: layout.slots.map(s => {
                if (s.id === slotId) {
                    return { ...s, rowEnd: s.rowEnd + 1 };
                }
                if (s.rowStart > slot.rowStart) {
                    return { ...s, rowStart: s.rowStart + 1, rowEnd: s.rowEnd + 1 };
                }
                return s;
            }),
        };
        onLayoutChange(newLayout);
    }, [layout, slots, onLayoutChange]);
    // Split slot vertically
    const splitSlotVertical = useCallback((slotId) => {
        const slot = slots.find(s => s.id === slotId);
        if (!slot)
            return;
        const newLayout = {
            ...layout,
            cols: layout.cols + 1,
            colSizes: [...layout.colSizes, '1fr'],
            slots: layout.slots.map(s => {
                if (s.id === slotId) {
                    return { ...s, colEnd: s.colEnd + 1 };
                }
                if (s.colStart > slot.colStart) {
                    return { ...s, colStart: s.colStart + 1, colEnd: s.colEnd + 1 };
                }
                return s;
            }),
        };
        onLayoutChange(newLayout);
    }, [layout, slots, onLayoutChange]);
    // Merge two adjacent slots into one
    const mergeSlots = useCallback((slotId1, slotId2) => {
        const slot1 = layout.slots.find(s => s.id === slotId1);
        const slot2 = layout.slots.find(s => s.id === slotId2);
        if (!slot1 || !slot2)
            return;
        // Validate adjacency: same row span OR same col span, borders touching
        const sameRowSpan = slot1.colStart === slot2.colStart && slot1.colEnd === slot2.colEnd;
        const sameColSpan = slot1.rowStart === slot2.rowStart && slot1.rowEnd === slot2.rowEnd;
        const verticallyAdjacent = sameRowSpan && (slot1.rowEnd === slot2.rowStart || slot2.rowEnd === slot1.rowStart);
        const horizontallyAdjacent = sameColSpan && (slot1.colEnd === slot2.colStart || slot2.colEnd === slot1.colStart);
        if (!verticallyAdjacent && !horizontallyAdjacent)
            return;
        const mergedSlot = {
            id: slot1.id,
            rowStart: Math.min(slot1.rowStart, slot2.rowStart),
            rowEnd: Math.max(slot1.rowEnd, slot2.rowEnd),
            colStart: Math.min(slot1.colStart, slot2.colStart),
            colEnd: Math.max(slot1.colEnd, slot2.colEnd),
            symbol: slot1.symbol,
            timeframe: slot1.timeframe,
            symbolLinkGroup: slot1.symbolLinkGroup,
            areaName: slot1.areaName,
        };
        const newSlots = layout.slots
            .filter(s => s.id !== slotId2)
            .map(s => s.id === slot1.id ? mergedSlot : s);
        // Rebuild templateAreas from updated slots
        const newTemplateAreas = Array(layout.rows).fill(null).map((_, r) => Array(layout.cols).fill(null).map((_, c) => {
            const match = newSlots.find(s => s.rowStart <= r + 1 && s.rowEnd >= r + 1 && s.colStart <= c + 1 && s.colEnd >= c + 1);
            return match?.areaName ?? '.';
        }).join(' '));
        const newAreas = { ...layout.areas };
        if (slot2.areaName)
            delete newAreas[slot2.areaName];
        onLayoutChange({
            ...layout,
            slots: newSlots,
            templateAreas: newTemplateAreas,
            areas: newAreas,
        });
    }, [layout, onLayoutChange]);
    // Save as preset
    const [presetError, setPresetError] = useState(null);
    const handleSavePreset = useCallback(() => {
        if (!presetName.trim())
            return;
        const preset = {
            id: `preset-${Date.now()}`,
            name: presetName.trim(),
            layout,
            category: 'custom',
        };
        // P0: chỉ đóng modal khi preset **thực sự** được lưu. Không có handler hoặc handler
        // trả false ⇒ giữ modal mở, giữ tên đã gõ, và báo lỗi cho người dùng.
        const saved = onSaveAsPreset?.(preset);
        if (!onSaveAsPreset || saved === false) {
            setPresetError(t('layoutDesigner.savePresetFailed') ?? 'Could not save the preset. Please try again.');
            return;
        }
        setPresetError(null);
        setShowPresetModal(false);
        setPresetName("");
    }, [presetName, layout, onSaveAsPreset, t]);
    // Sync slots/sizes when rows change
    const handleRowsChange = useCallback((newRows) => {
        const clamped = Math.max(1, Math.min(10, newRows));
        if (clamped === layout.rows)
            return;
        const delta = clamped - layout.rows;
        let newSlots = [...layout.slots];
        const newRowSizes = [...layout.rowSizes];
        if (delta > 0) {
            for (let i = 0; i < delta; i++) {
                newRowSizes.push('1fr');
                for (let c = 0; c < layout.cols; c++) {
                    const rowIdx = clamped - delta + i;
                    const areaName = `chart-${rowIdx * layout.cols + c + 1}`;
                    newSlots.push({
                        id: areaName,
                        rowStart: rowIdx + 1,
                        rowEnd: rowIdx + 1,
                        colStart: c + 1,
                        colEnd: c + 1,
                        areaName,
                    });
                }
            }
        }
        else {
            newRowSizes.splice(clamped);
            newSlots = newSlots
                .filter(s => s.rowStart <= clamped)
                .map(s => s.rowEnd > clamped ? { ...s, rowEnd: clamped } : s);
        }
        const newTemplateAreas = Array(clamped).fill(null).map((_, r) => Array(layout.cols).fill(null).map((_, c) => {
            const match = newSlots.find(s => s.rowStart <= r + 1 && s.rowEnd >= r + 1 && s.colStart <= c + 1 && s.colEnd >= c + 1);
            return match?.areaName ?? '.';
        }).join(' '));
        onLayoutChange({ ...layout, rows: clamped, rowSizes: newRowSizes, slots: newSlots, templateAreas: newTemplateAreas });
    }, [layout, onLayoutChange]);
    // Sync slots/sizes when cols change
    const handleColsChange = useCallback((newCols) => {
        const clamped = Math.max(1, Math.min(10, newCols));
        if (clamped === layout.cols)
            return;
        const delta = clamped - layout.cols;
        let newSlots = [...layout.slots];
        const newColSizes = [...layout.colSizes];
        if (delta > 0) {
            for (let i = 0; i < delta; i++) {
                newColSizes.push('1fr');
                for (let r = 0; r < layout.rows; r++) {
                    const colIdx = clamped - delta + i;
                    const areaName = `chart-${r * clamped + colIdx + 1}`;
                    newSlots.push({
                        id: areaName,
                        rowStart: r + 1,
                        rowEnd: r + 1,
                        colStart: colIdx + 1,
                        colEnd: colIdx + 1,
                        areaName,
                    });
                }
            }
        }
        else {
            newColSizes.splice(clamped);
            newSlots = newSlots
                .filter(s => s.colStart <= clamped)
                .map(s => s.colEnd > clamped ? { ...s, colEnd: clamped } : s);
        }
        const newTemplateAreas = Array(layout.rows).fill(null).map((_, r) => Array(clamped).fill(null).map((_, c) => {
            const match = newSlots.find(s => s.rowStart <= r + 1 && s.rowEnd >= r + 1 && s.colStart <= c + 1 && s.colEnd >= c + 1);
            return match?.areaName ?? '.';
        }).join(' '));
        onLayoutChange({ ...layout, cols: clamped, colSizes: newColSizes, slots: newSlots, templateAreas: newTemplateAreas });
    }, [layout, onLayoutChange]);
    if (!isOpen)
        return null;
    return (_jsx("div", { className: `${styles.overlay} ${styles.animFadeIn}`, onClick: onClose, role: "dialog", "aria-modal": "true", "aria-labelledby": "layout-designer-title", children: _jsxs("div", { className: `${styles.modal} ${styles.animPopIn}`, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: styles.header, children: [_jsx("h2", { id: "layout-designer-title", className: styles.title, children: t('layoutDesigner.title') ?? 'Layout Designer' }), _jsxs("div", { className: styles.headerActions, children: [_jsx("button", { className: styles.presetButton, "data-testid": "layout-designer-save-as-preset", onClick: () => setShowPresetModal(true), children: t('layoutDesigner.saveAsPreset') ?? 'Save as Preset' }), _jsx("button", { className: styles.closeButton, onClick: onClose, children: "\u00D7" })] })] }), _jsxs("div", { className: styles.toolbar, children: [_jsxs("div", { className: styles.toolbarGroup, children: [_jsx("label", { className: styles.toolbarLabel, children: t('layoutDesigner.rows') ?? 'Rows' }), _jsx("input", { type: "number", min: 1, max: 10, value: layout.rows, onChange: (e) => handleRowsChange(parseInt(e.target.value)), className: styles.toolbarInput })] }), _jsxs("div", { className: styles.toolbarGroup, children: [_jsx("label", { className: styles.toolbarLabel, children: t('layoutDesigner.cols') ?? 'Cols' }), _jsx("input", { type: "number", min: 1, max: 10, value: layout.cols, onChange: (e) => handleColsChange(parseInt(e.target.value)), className: styles.toolbarInput })] }), _jsxs("div", { className: styles.toolbarGroup, children: [_jsx("button", { className: styles.actionButton, onClick: () => handleRowsChange(layout.rows + 1), children: t('layoutDesigner.addRow') ?? 'Add Row' }), _jsx("button", { className: styles.actionButton, onClick: () => handleColsChange(layout.cols + 1), children: t('layoutDesigner.addCol') ?? 'Add Col' })] }), availablePresets.length > 0 && (_jsxs("div", { className: styles.toolbarGroup, "data-testid": "layout-designer-saved-presets", children: [_jsx("span", { className: styles.savedPresetsLabel, children: t('layoutDesigner.savedPresets') ?? 'Saved presets' }), availablePresets.map(p => (_jsxs("span", { className: styles.savedPresetChip, children: [_jsx("button", { type: "button", className: styles.actionButton, "data-testid": `layout-preset-apply-${p.id}`, onClick: () => onApplyPreset?.(p), disabled: !onApplyPreset, title: p.name, children: p.name }), onDeletePreset && (_jsx("button", { type: "button", className: styles.actionButton, "data-testid": `layout-preset-delete-${p.id}`, onClick: () => onDeletePreset(p), "aria-label": t('layoutDesigner.deletePreset') ?? 'Delete preset', title: t('layoutDesigner.deletePreset') ?? 'Delete preset', children: "\u00D7" }))] }, p.id)))] }))] }), _jsx("div", { className: styles.gridPreview, ref: containerRef, children: _jsx("div", { className: styles.gridContainer, style: {
                            gridTemplateAreas: templateAreas.map(row => `"${row}"`).join(" "),
                            gridTemplateRows: layout.rowSizes.join(" "),
                            gridTemplateColumns: layout.colSizes.join(" "),
                        }, children: slots.map((slot) => (_jsxs("div", { className: `${styles.slot} ${selectedSlotId === slot.id ? styles.selected : ''}`, style: {
                                gridArea: slot.areaName,
                                zIndex: slot.zIndex ?? 1,
                            }, onClick: () => setSelectedSlotId(slot.id), children: [_jsxs("div", { className: styles.slotContent, children: [_jsx("span", { className: styles.slotSymbol, children: slot.symbol ?? '—' }), _jsx("span", { className: styles.slotTimeframe, children: slot.timeframe ?? '—' })] }), _jsx("div", { className: `${styles.resizeHandle} ${styles.resizeTop}`, onMouseDown: (e) => handleSlotMouseDown(e, slot, 'top'), "aria-label": t('layoutDesigner.resizeTop') ?? 'Resize top' }), _jsx("div", { className: `${styles.resizeHandle} ${styles.resizeRight}`, onMouseDown: (e) => handleSlotMouseDown(e, slot, 'right'), "aria-label": t('layoutDesigner.resizeRight') ?? 'Resize right' }), _jsx("div", { className: `${styles.resizeHandle} ${styles.resizeBottom}`, onMouseDown: (e) => handleSlotMouseDown(e, slot, 'bottom'), "aria-label": t('layoutDesigner.resizeBottom') ?? 'Resize bottom' }), _jsx("div", { className: `${styles.resizeHandle} ${styles.resizeLeft}`, onMouseDown: (e) => handleSlotMouseDown(e, slot, 'left'), "aria-label": t('layoutDesigner.resizeLeft') ?? 'Resize left' }), _jsxs("div", { className: styles.splitButtons, children: [_jsx("button", { className: styles.splitButton, onClick: (e) => {
                                                e.stopPropagation();
                                                splitSlotHorizontal(slot.id);
                                            }, "aria-label": t('layoutDesigner.splitHorizontal') ?? 'Split horizontally', children: "\u2796" }), _jsx("button", { className: styles.splitButton, onClick: (e) => {
                                                e.stopPropagation();
                                                splitSlotVertical(slot.id);
                                            }, "aria-label": t('layoutDesigner.splitVertical') ?? 'Split vertically', children: "\u2795" }), selectedSlotId && selectedSlotId !== slot.id && (_jsx("button", { className: styles.splitButton, onClick: (e) => {
                                                e.stopPropagation();
                                                mergeSlots(selectedSlotId, slot.id);
                                                setSelectedSlotId(null);
                                            }, "aria-label": t('layoutDesigner.mergeSlots') ?? 'Merge slots', children: "\uD83D\uDD17" }))] })] }, slot.id))) }) }), showPresetModal && (_jsx("div", { className: `${styles.presetModalOverlay} ${styles.animFadeIn}`, onClick: () => setShowPresetModal(false), children: _jsxs("div", { className: `${styles.presetModal} ${styles.animPopIn}`, onClick: (e) => e.stopPropagation(), children: [_jsx("h3", { children: t('layoutDesigner.savePresetTitle') ?? 'Save as Preset' }), _jsx("input", { type: "text", value: presetName, onChange: (e) => setPresetName(e.target.value), placeholder: t('layoutDesigner.presetNamePlaceholder') ?? 'Enter preset name', className: styles.presetInput, "data-testid": "layout-designer-preset-name", autoFocus: true }), presetError && (_jsx("p", { role: "alert", className: styles.presetError, children: presetError })), _jsxs("div", { className: styles.presetActions, children: [_jsx("button", { className: styles.cancelButton, onClick: () => setShowPresetModal(false), children: t('common.cancel') ?? 'Cancel' }), _jsx("button", { className: styles.saveButton, "data-testid": "layout-designer-preset-save", onClick: handleSavePreset, children: t('common.save') ?? 'Save' })] })] }) }))] }) }));
};
export default LayoutDesigner;
